import { useEffect, useId, useRef, useState } from 'react';
import Tooltip from '@/components/ui/Tooltip';

import Button from '@/components/ui/Button';
import InputField from '@/components/ui/Input';
import Modal from '@/components/ui/Modal';
import Textarea from '@/components/ui/Textarea';

export type OtherEvidencePayload = {
  text: string | null;
  contact_name: string | null;
  contact_details: string | null;
  external_link: string | null;
};

export type OtherEvidenceModalMode =
  | { kind: 'create' }
  | {
      kind: 'edit';
      initialText: string | null;
      initialContactName: string | null;
      initialContactDetails: string | null;
      initialExternalLink: string | null;
    };

interface OtherEvidenceModalProps {
  open: boolean;
  onClose: () => void;
  mode: OtherEvidenceModalMode;
  onSave: (payload: OtherEvidencePayload) => Promise<void>;
  title?: string;
}

/**
 * Shared note / contact / external-link form used by PAM and group Evidence.
 */
export default function OtherEvidenceModal({ open, onClose, mode, onSave, title }: OtherEvidenceModalProps) {
  const baseId = useId();
  const noteId = `${baseId}-note`;
  const contactNameId = `${baseId}-contact-name`;
  const contactDetailsId = `${baseId}-contact-details`;
  const linkId = `${baseId}-link`;

  const [text, setText] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactDetails, setContactDetails] = useState('');
  const [externalLink, setExternalLink] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const modeRef = useRef(mode);
  modeRef.current = mode;

  useEffect(() => {
    if (!open) return;
    const current = modeRef.current;
    if (current.kind === 'edit') {
      setText(current.initialText ?? '');
      setContactName(current.initialContactName ?? '');
      setContactDetails(current.initialContactDetails ?? '');
      setExternalLink(current.initialExternalLink ?? '');
    } else {
      setText('');
      setContactName('');
      setContactDetails('');
      setExternalLink('');
    }
    setError(null);
  }, [open]);

  const hasPayload = text.trim().length > 0 || externalLink.trim().length > 0;
  const heading = title ?? (mode.kind === 'edit' ? 'Edit evidence' : 'Add other evidence');

  function handleClose() {
    if (submitting) return;
    onClose();
  }

  async function handleSave() {
    if (!hasPayload || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      await onSave({
        text: text.trim() || null,
        contact_name: contactName.trim() || null,
        contact_details: contactDetails.trim() || null,
        external_link: externalLink.trim() || null
      });
      onClose();
    } catch {
      setError('Something went wrong saving the evidence. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={<h2 className="text-lg font-semibold text-content-default">{heading}</h2>}
      className="w-full max-w-lg"
      aria-label={heading}
    >
      <div className="mt-2 flex flex-col gap-5">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={linkId} className="text-sm font-medium text-content-default">
            External link
          </label>
          <InputField
            id={linkId}
            type="text"
            value={externalLink}
            onChange={(e) => setExternalLink(e.target.value)}
            maxLength={2000}
            placeholder="https://…"
            fullWidth
          />
          <span className="text-xs text-content-subtle">
            Link to SharePoint, a CAFM system, or any external resource.
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor={noteId} className="text-sm font-medium text-content-default">
            Note
          </label>
          <Textarea
            id={noteId}
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            maxLength={5000}
            characterLimit={5000}
            placeholder="Add a free-text note explaining where evidence can be found, why it is missing, or any other context…"
            fullWidth
          />
        </div>

        <div className="flex flex-col gap-3">
          <span className="text-sm font-medium text-content-default">
            Contact <span className="font-normal text-content-subtle">optional</span>
          </span>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={contactNameId} className="text-xs font-medium text-content-subtle">
              Name
            </label>
            <InputField
              id={contactNameId}
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              maxLength={500}
              placeholder="e.g. Dr A. Cleaver"
              fullWidth
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor={contactDetailsId} className="text-xs font-medium text-content-subtle">
              Details
            </label>
            <InputField
              id={contactDetailsId}
              type="text"
              value={contactDetails}
              onChange={(e) => setContactDetails(e.target.value)}
              maxLength={500}
              placeholder="e.g. email, phone"
              fullWidth
            />
          </div>
        </div>

        {error !== null && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 border-t border-border-subtle pt-3">
          <Button variant="ghost" text="Cancel" onClick={handleClose} disabled={submitting} />
          <Tooltip title="Fill in at least a note or an external link to save." disableHoverListener={hasPayload}>
            <span>
              <Button
                variant="primary"
                text="Save"
                onClick={() => void handleSave()}
                loading={submitting}
                disabled={!hasPayload}
              />
            </span>
          </Tooltip>
        </div>
      </div>
    </Modal>
  );
}
